import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import PersonIcon from '@mui/icons-material/Person';
import PageShell from '../components/PageShell';
import CustomerNav from '../components/CustomerNav';
import CustomerStatusBar from '../components/CustomerStatusBar';
import StoreLogoBadge from '../components/StoreLogoBadge';
import { useImagePreview } from '../components/ImagePreview';
import {
    BidForm,
    AuctionClock,
    HIGHLIGHT_DURATION_MS,
    bidSlideIn,
    bidGlowPulse,
    amountPop,
    badgeFade,
} from '../components/BidWidgets';
import request, { productSocketUrl } from '../api';
import { formatDateTime, isEnded } from '../dateUtils';
import { formatMXN } from '../numberUtils';

// Bid updates arrive instantly over the WebSocket; this is just a safety-net
// re-sync in case a connection drops silently without firing onclose.
const POLL_FALLBACK_MS = 10000;
const RECONNECT_DELAY_MS = 2000;

function ProductDetail() {
    const { id } = useParams();
    const { openPreview } = useImagePreview();
    const [token] = useState(localStorage.getItem('customerToken'));
    const [customer, setCustomer] = useState(null);
    const [product, setProduct] = useState(null);
    const [bids, setBids] = useState([]);
    const [notFound, setNotFound] = useState(false);
    const [live, setLive] = useState(false);
    const [newBidId, setNewBidId] = useState(null);
    const [pulseKey, setPulseKey] = useState(0);
    const wsRef = useRef(null);
    const highlightTimerRef = useRef(null);

    const loadProduct = useCallback(() => {
        if (!token) return;
        request(`/products/${id}`, { token })
            .then(setProduct)
            .catch(() => setNotFound(true));
    }, [id, token]);

    const loadBids = useCallback(() => {
        if (!token) return;
        request(`/products/${id}/bids`, { token }).then(setBids).catch(() => {});
    }, [id, token]);

    const loadMe = useCallback(() => {
        if (!token) return;
        request('/customer/me', { token }).then(setCustomer).catch(() => setCustomer(null));
    }, [token]);

    useEffect(() => {
        if (!token) return;
        loadProduct();
        loadBids();
        loadMe();
        const interval = setInterval(() => {
            loadProduct();
            loadBids();
            loadMe();
        }, POLL_FALLBACK_MS);
        return () => clearInterval(interval);
    }, [token, loadProduct, loadBids, loadMe]);

    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        let reconnectTimer = null;

        function connect() {
            const ws = new WebSocket(productSocketUrl(id, token));
            wsRef.current = ws;

            ws.onopen = () => {
                if (!cancelled) setLive(true);
            };

            ws.onmessage = (event) => {
                let msg;
                try {
                    msg = JSON.parse(event.data);
                } catch {
                    return;
                }
                if (msg.type === 'bid_placed') {
                    setProduct(msg.product);
                    setBids((prev) => {
                        if (prev.some((b) => b.id === msg.bid.id)) return prev;
                        setNewBidId(msg.bid.id);
                        setPulseKey((k) => k + 1);
                        clearTimeout(highlightTimerRef.current);
                        highlightTimerRef.current = setTimeout(() => setNewBidId(null), HIGHLIGHT_DURATION_MS);
                        return [msg.bid, ...prev];
                    });
                }
            };

            ws.onclose = () => {
                if (cancelled) return;
                setLive(false);
                reconnectTimer = setTimeout(connect, RECONNECT_DELAY_MS);
            };

            ws.onerror = () => ws.close();
        }

        connect();

        return () => {
            cancelled = true;
            clearTimeout(reconnectTimer);
            clearTimeout(highlightTimerRef.current);
            if (wsRef.current) wsRef.current.close();
        };
    }, [id, token]);

    const handleBid = () => {
        loadProduct();
        loadBids();
        loadMe();
    };

    if (!token) {
        return (
            <PageShell
                title="Inicio de Sesión Requerido"
                nav={<Button component={RouterLink} to="/auctions" color="inherit">Volver a Subastas</Button>}
                maxWidth="sm"
            >
                <Alert severity="info">
                    <RouterLink to="/auctions" style={{ color: 'inherit', fontWeight: 700 }}>Inicia sesión o regístrate</RouterLink> para ver los detalles de esta subasta.
                </Alert>
            </PageShell>
        );
    }

    if (notFound) {
        return (
            <PageShell title="Producto No Encontrado" nav={<CustomerNav />} maxWidth="sm">
                <Alert severity="error">Este producto no existe o fue eliminado.</Alert>
            </PageShell>
        );
    }

    if (!product) {
        return (
            <PageShell title="Cargando..." nav={<CustomerNav />} maxWidth="sm">
                <Typography color="text.secondary">Cargando detalles de la subasta...</Typography>
            </PageShell>
        );
    }

    const ended = isEnded(product.termination_time);
    const currentBid = product.current_bid != null ? Number(product.current_bid) : Number(product.starting_price);

    return (
        <PageShell title={product.title} nav={<CustomerNav />}>
            <Grid
                container
                spacing={4}
                sx={customer ? { mb: { xs: 'calc(72px + env(safe-area-inset-bottom))', sm: '84px' } } : undefined}
            >
                <Grid item xs={12} md={7}>
                    <Paper sx={{ overflow: 'hidden' }}>
                        {product.image_url && (
                            <Box sx={{ position: 'relative' }}>
                                <Box
                                    component="img"
                                    src={product.image_url}
                                    alt={product.title}
                                    onClick={() => openPreview(product.image_url, product.title)}
                                    sx={{ width: '100%', maxHeight: 360, objectFit: 'cover', display: 'block', cursor: 'zoom-in' }}
                                />
                                <StoreLogoBadge src={product.store_logo_url} alt={product.store_name} />
                            </Box>
                        )}
                        <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                Vendido por {product.store_name}
                            </Typography>
                            {product.description && (
                                <Typography sx={{ mb: 2 }}>{product.description}</Typography>
                            )}
                            <Chip
                                size="small"
                                label={`${ended ? 'Subasta finalizada' : 'Finaliza'}: ${formatDateTime(product.termination_time)}`}
                                color={ended ? 'default' : 'warning'}
                                variant="outlined"
                                sx={{ mb: 2 }}
                            />
                            <Stack direction="row" alignItems="center" spacing={1.5}>
                                {product.current_bid_username && (
                                    <Avatar src={product.current_bid_avatar_url || undefined} sx={{ width: 28, height: 28 }}>
                                        {!product.current_bid_avatar_url && <PersonIcon fontSize="small" />}
                                    </Avatar>
                                )}
                                <Typography
                                    key={pulseKey}
                                    variant="h5"
                                    fontWeight={700}
                                    gutterBottom
                                    sx={{ mb: 0, display: 'inline-block', animation: pulseKey ? `${amountPop} 500ms ease-out` : 'none' }}
                                >
                                    {formatMXN(currentBid)}
                                    {product.current_bid_username && (
                                        <Typography component="span" variant="body1" color="text.secondary" sx={{ ml: 1.5 }}>
                                            por {product.current_bid_username}
                                        </Typography>
                                    )}
                                </Typography>
                            </Stack>

                            {customer && !ended && <BidForm product={product} token={token} onBid={handleBid} />}
                            {ended && (
                                <Alert severity="info" sx={{ mt: 2 }}>
                                    Esta subasta ha finalizado y ya no acepta pujas.
                                </Alert>
                            )}
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12} md={5}>
                    <Paper sx={{ p: { xs: 2.5, sm: 3 } }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <Typography variant="h6">Historial de Pujas</Typography>
                            <Chip
                                size="small"
                                label={live ? 'En vivo' : 'Reconectando…'}
                                color={live ? 'success' : 'default'}
                            />
                        </Stack>
                        <AuctionClock startTime={product.starts_at || product.created_at} endTime={product.termination_time} />
                        {bids.length === 0 && (
                            <Typography color="text.secondary">Aún no hay pujas. ¡Sé el primero!</Typography>
                        )}
                        <List disablePadding>
                            {bids.map((bid, i) => {
                                const isNew = bid.id === newBidId;
                                return (
                                    <React.Fragment key={bid.id}>
                                        <ListItem
                                            disableGutters
                                            sx={{
                                                borderRadius: '5px',
                                                width: '100%',
                                                boxSizing: 'border-box',
                                                px: isNew ? 1 : 0,
                                                mx: isNew ? -1 : 0,
                                                transformOrigin: 'top center',
                                                ...(isNew && {
                                                    animation: `${bidSlideIn} 420ms cubic-bezier(0.22, 1, 0.36, 1), ${bidGlowPulse} ${HIGHLIGHT_DURATION_MS}ms ease-out`,
                                                }),
                                            }}
                                        >
                                            <ListItemAvatar>
                                                <Avatar src={bid.avatar_url || undefined} sx={{ bgcolor: 'rgba(15,23,42,0.06)' }}>
                                                    {!bid.avatar_url && <PersonIcon fontSize="small" />}
                                                </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        <span>{bid.username}</span>
                                                        {isNew && (
                                                            <Chip
                                                                label="Nueva puja"
                                                                size="small"
                                                                color="primary"
                                                                sx={{
                                                                    height: 18,
                                                                    fontSize: '0.65rem',
                                                                    animation: `${badgeFade} ${HIGHLIGHT_DURATION_MS}ms ease-in-out`,
                                                                }}
                                                            />
                                                        )}
                                                    </Stack>
                                                }
                                                secondary={formatDateTime(bid.created_at)}
                                            />
                                            <Typography fontWeight={700} color="secondary.main">
                                                {formatMXN(bid.amount)}
                                            </Typography>
                                        </ListItem>
                                        {i < bids.length - 1 && <Divider component="li" sx={{ borderColor: 'divider' }} />}
                                    </React.Fragment>
                                );
                            })}
                        </List>
                    </Paper>
                </Grid>
            </Grid>

            {customer && <CustomerStatusBar customer={customer} />}
        </PageShell>
    );
}

export default ProductDetail;
